import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

export const LoadingSpinner: React.FC = () => {
  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="h-10 w-10 rounded-full bg-muted animate-pulse" />
    </div>
  );
};

export const WeekDetailSkeleton: React.FC = () => {
  return (
    <div className="py-6 space-y-6">
      <Skeleton className="h-10 w-2/5" />

      <Card>
        <CardContent className="pt-6 space-y-4">
          <Skeleton className="h-8 w-3/5" />
          <Skeleton className="h-6 w-2/5" />
        </CardContent>
      </Card>

      {[0, 1, 2].map((i) => (
        <Card key={i}>
          <CardHeader className="pb-2">
            <Skeleton className="h-6 w-[min(100%,20rem)]" />
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <Skeleton className="h-4 w-full max-w-md" />
            <Skeleton className="h-4 w-4/5 max-w-sm" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export const WeekCardSkeleton: React.FC = () => {
  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-9 w-30" />
      </CardContent>
    </Card>
  );
};

/** Форма «Новая неделя»: дата + кнопки (пока идёт создание). */
export const CreateWeekFormSkeleton: React.FC = () => {
  return (
    <div className="space-y-6" data-testid="create-week-loading">
      <Card>
        <CardHeader>
          <Skeleton className="h-7 w-44 max-w-[min(100%,12rem)]" />
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-11 w-full max-w-xs rounded-xl" />
            <Skeleton className="h-3 w-full max-w-lg" />
          </div>
          <div className="flex gap-2 pt-2">
            <Skeleton className="h-11 min-h-[44px] flex-1 rounded-full" />
            <Skeleton className="h-11 min-h-[44px] flex-1 rounded-full" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/** Список недель в архиве */
export const ArchiveWeekListSkeleton: React.FC = () => {
  return (
    <ul className="space-y-3" data-testid="archive-loading-skeleton">
      {[0, 1, 2, 3, 4].map((i) => (
        <li key={i}>
          <Card>
            <CardHeader className="pb-2">
              <Skeleton className="h-6 w-[min(100%,28rem)]" />
            </CardHeader>
            <CardContent className="pt-0">
              <Skeleton className="h-3 w-28" />
            </CardContent>
          </Card>
        </li>
      ))}
    </ul>
  );
};

/** Список дней в журнале */
export const JournalDatesListSkeleton: React.FC = () => {
  return (
    <ul className="space-y-2" data-testid="journal-loading-skeleton">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <li key={i}>
          <Card>
            <CardContent className="flex items-center gap-3 py-4">
              <Skeleton className="h-5 w-5 shrink-0 rounded" />
              <Skeleton className="h-5 flex-1 max-w-md" />
            </CardContent>
          </Card>
        </li>
      ))}
    </ul>
  );
};

/** Контент трекера под выбором даты: контекст недели, прогресс, сетка карточек */
export const TrackerBelowDateSkeleton: React.FC = () => {
  return (
    <div className="space-y-4" data-testid="tracker-loading-skeleton">
      <Skeleton className="h-[4.25rem] w-full rounded-2xl" />
      <Skeleton className="h-16 w-full rounded-2xl" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {[0, 1, 2, 3, 4].map((i) => (
          <Card key={i} className={i === 4 ? 'sm:col-span-2' : ''}>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Skeleton className="h-6 w-6 rounded" />
                <Skeleton className="h-6 w-48 max-w-[min(100%,12rem)]" />
              </div>
            </CardHeader>
            <CardContent>
              <Skeleton className="h-4 w-full max-w-xs" />
              <Skeleton className="mt-2 h-3 w-28" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

/** Трекер: заголовок, дата, сетка карточек (первый заход без шапки с датой снаружи) */
export const TrackerPageSkeleton: React.FC = () => {
  return (
    <div className="space-y-6" data-testid="tracker-loading-skeleton-full">
      <Skeleton className="h-9 w-36" />
      <div className="mb-6 space-y-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-10 w-full max-w-xs" />
        <Skeleton className="h-3 w-full max-w-lg" />
      </div>
      <TrackerBelowDateSkeleton />
    </div>
  );
};

/** Страницы трекинга (слайдеры / оценки) */
export const TrackingPageSkeleton: React.FC = () => {
  return (
    <div className="space-y-6" data-testid="tracking-page-skeleton">
      <Skeleton className="h-9 w-28" />
      <Skeleton className="h-9 w-[min(100%,20rem)]" />
      <div className="mb-6 space-y-2">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-10 w-full max-w-xs" />
      </div>
      {[0, 1, 2].map((i) => (
        <Card key={i}>
          <CardContent className="space-y-4 pt-6">
            <Skeleton className="h-6 w-full max-w-lg" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-4 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

/** Журнал самоисследования за день */
export const SelfEnquiryTrackingSkeleton: React.FC = () => {
  return (
    <div className="space-y-6" data-testid="self-enquiry-tracking-skeleton">
      <Skeleton className="h-9 w-28" />
      <Skeleton className="h-9 w-[min(100%,24rem)]" />
      <div className="mb-6 space-y-2">
        <Skeleton className="h-4 w-14" />
        <Skeleton className="h-10 w-full max-w-xs" />
      </div>
      <div className="space-y-3">
        {[0, 1].map((i) => (
          <Card key={i}>
            <CardContent className="py-3 space-y-2">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-16 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    </div>
  );
};

/** Обзор недели в архиве */
export const ArchiveWeekReviewSkeleton: React.FC = () => {
  return (
    <div className="space-y-6" data-testid="archive-week-review-skeleton">
      <Skeleton className="h-5 w-72 max-w-full" />
      <div className="flex flex-wrap gap-2">
        <Skeleton className="h-9 w-44" />
        <Skeleton className="h-9 w-52" />
      </div>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <Card key={i}>
          <CardHeader className="py-3">
            <Skeleton className="h-5 w-48" />
          </CardHeader>
          <CardContent className="space-y-2 p-6 pt-0">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
